import { useEffect, useRef, useState } from "react";

export default function LineConsult() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText("@longjing").then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const consultTopics = [
    { icon: "ri-ancient-gate-line", label: "天珠選購諮詢", color: "#8b6f47" },
    { icon: "ri-sparkling-2-line", label: "水晶能量搭配", color: "#7a6a9a" },
    { icon: "ri-leaf-line", label: "玉石鑑賞建議", color: "#4a7a52" },
    { icon: "ri-heart-3-line", label: "唐卡收藏指南", color: "#c9a96e" },
    { icon: "ri-gift-line", label: "禮品訂製服務", color: "#8b6f47" },
    { icon: "ri-question-answer-line", label: "能量飾品入門", color: "#7a6a9a" },
  ];

  return (
    <section className="py-20 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-16">
        {/* Section Header */}
        <div
          className={`text-center mb-14 transition-all duration-1000 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="w-10 h-px bg-[#8b6f47]"></span>
            <span
              className="text-[#8b6f47] text-[1rem] tracking-[0.3em] font-medium"
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              LINE CONSULT
            </span>
            <span className="w-10 h-px bg-[#8b6f47]"></span>
          </div>
          <h2
            className="text-[#2d2520] font-bold mb-4 leading-[1.4]"
            style={{ fontSize: "clamp(1.5rem, 3vw, 2.125rem)", fontFamily: "'Noto Serif TC', serif" }}
          >
            LINE 即時線上諮詢
          </h2>
          <p
            className="text-[#7a6a5a] max-w-xl mx-auto leading-[1.75]"
            style={{ fontSize: "1.0625rem", fontFamily: "'Noto Serif TC', serif" }}
          >
            掃描 QR Code 或搜尋 LINE ID，即可與我們的能量飾品顧問即時對話，獲得最專業的選購建議。
          </p>
        </div>

        <div
          className={`transition-all duration-1000 delay-200 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-10 items-start">
            {/* Left: QR Code Card */}
            <div className="bg-[#1e1a16] rounded-3xl overflow-hidden">
              {/* Top: LINE Brand */}
              <div className="flex items-center gap-3 px-8 py-6 border-b border-white/10">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#06C755]">
                  <i className="ri-line-line text-white text-xl"></i>
                </div>
                <div>
                  <p
                    className="text-white font-semibold text-[1.0625rem]"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    瓏靖手作能量
                  </p>
                  <p className="text-white/50 text-[0.875rem]" style={{ fontFamily: "'Noto Serif TC', serif" }}>
                    LINE 官方帳號
                  </p>
                </div>
                <div className="ml-auto">
                  <span className="px-3 py-1 bg-[#06C755]/20 text-[#06C755] text-[0.8125rem] rounded-full font-medium" style={{ fontFamily: "'Noto Serif TC', serif" }}>
                    線上中
                  </span>
                </div>
              </div>

              {/* QR Code Area */}
              <div className="px-8 py-10 flex flex-col items-center">
                {/* QR Code Placeholder — using a decorative pattern */}
                <div className="w-52 h-52 bg-white rounded-2xl p-4 mb-6 flex items-center justify-center relative overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=LINE%20QR%20code%20scan%20pattern%20black%20white%20square%20grid%20minimal%20clean%20simple%20graphic%20design%20on%20white%20background&width=200&height=200&seq=qrcode_line001&orientation=squarish"
                    alt="LINE QR Code"
                    className="w-full h-full object-cover rounded-lg"
                  />
                  {/* LINE logo overlay center */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#06C755]">
                      <i className="ri-line-line text-white text-2xl"></i>
                    </div>
                  </div>
                </div>

                <p
                  className="text-white/70 text-[0.875rem] mb-5 text-center"
                  style={{ fontFamily: "'Noto Serif TC', serif" }}
                >
                  使用 LINE App 掃描 QR Code<br />即可加入好友開始諮詢
                </p>

                {/* LINE ID Copy */}
                <div className="w-full flex items-center gap-3 bg-white/10 rounded-xl px-5 py-3.5 mb-5">
                  <i className="ri-at-line text-[#c9a96e] text-lg flex-shrink-0"></i>
                  <span
                    className="text-white font-medium flex-1 text-[1rem]"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    @longjing
                  </span>
                  <button
                    onClick={handleCopy}
                    className={`px-3 py-1.5 rounded-lg text-[0.8125rem] font-medium cursor-pointer transition-all duration-200 whitespace-nowrap ${
                      copied
                        ? "bg-[#06C755] text-white"
                        : "bg-white/20 text-white/80 hover:bg-white/30"
                    }`}
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    {copied ? "已複製！" : "複製 ID"}
                  </button>
                </div>

                <a
                  href="https://line.me/R/ti/p/@longjing"
                  rel="nofollow"
                  target="_blank"
                  className="w-full flex items-center justify-center gap-2 py-4 bg-[#06C755] text-white rounded-xl text-[1rem] font-semibold cursor-pointer hover:bg-[#05b34c] transition-colors whitespace-nowrap"
                  style={{ fontFamily: "'Noto Serif TC', serif" }}
                >
                  <i className="ri-line-line text-xl"></i>
                  立即開啟 LINE 諮詢
                </a>
              </div>

              {/* Bottom: Response Time */}
              <div className="px-8 py-5 bg-white/5 flex items-center gap-3">
                <i className="ri-time-line text-[#c9a96e] text-lg flex-shrink-0"></i>
                <p
                  className="text-white/60 text-[0.875rem] leading-[1.6]"
                  style={{ fontFamily: "'Noto Serif TC', serif" }}
                >
                  平均回覆時間：<strong className="text-white/80">30 分鐘內</strong>（週一至週六 10:00–20:00）
                </p>
              </div>
            </div>

            {/* Right: Consult Topics + Steps */}
            <div className="space-y-8">
              {/* Consult Topics */}
              <div>
                <h3
                  className="text-[#2d2520] font-bold mb-5"
                  style={{ fontSize: "1.25rem", fontFamily: "'Noto Serif TC', serif" }}
                >
                  可諮詢的主題
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {consultTopics.map((topic) => (
                    <div
                      key={topic.label}
                      className="flex items-center gap-3 p-4 bg-[#faf8f4] rounded-xl border border-[#e8e0d5] hover:border-[#8b6f47] transition-colors cursor-default"
                    >
                      <div
                        className="w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0"
                        style={{ backgroundColor: `${topic.color}18` }}
                      >
                        <i className={`${topic.icon} text-lg`} style={{ color: topic.color }}></i>
                      </div>
                      <span
                        className="text-[#5a4e45] text-[0.875rem] font-medium leading-[1.4]"
                        style={{ fontFamily: "'Noto Serif TC', serif" }}
                      >
                        {topic.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* How to Steps */}
              <div>
                <h3
                  className="text-[#2d2520] font-bold mb-5"
                  style={{ fontSize: "1.25rem", fontFamily: "'Noto Serif TC', serif" }}
                >
                  如何開始諮詢？
                </h3>
                <div className="space-y-4">
                  {[
                    {
                      step: "01",
                      title: "掃描 QR Code 或搜尋 LINE ID",
                      desc: "使用 LINE App 掃描左側 QR Code，或在 LINE 搜尋欄輸入「@longjing」加入好友。",
                    },
                    {
                      step: "02",
                      title: "傳送您的問題或需求",
                      desc: "告訴我們您想了解的商品、預算範圍，或是您希望飾品帶來的能量效果。",
                    },
                    {
                      step: "03",
                      title: "獲得專業的個人化建議",
                      desc: "我們的能量飾品顧問將根據您的需求，為您推薦最適合的商品與搭配方式。",
                    },
                  ].map((item) => (
                    <div key={item.step} className="flex items-start gap-5 p-5 bg-[#faf8f4] rounded-xl border border-[#e8e0d5]">
                      <div
                        className="w-10 h-10 flex items-center justify-center rounded-full bg-[#f0e8dc] flex-shrink-0 font-bold text-[#8b6f47] text-[0.875rem]"
                        style={{ fontFamily: "'Noto Serif TC', serif" }}
                      >
                        {item.step}
                      </div>
                      <div>
                        <h4
                          className="text-[#2d2520] font-semibold mb-1.5 text-[0.9375rem]"
                          style={{ fontFamily: "'Noto Serif TC', serif" }}
                        >
                          {item.title}
                        </h4>
                        <p
                          className="text-[#7a6a5a] text-[0.875rem] leading-[1.75]"
                          style={{ fontFamily: "'Noto Serif TC', serif" }}
                        >
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Testimonial Quote */}
              <div className="p-6 bg-[#f5f0e8] rounded-2xl border-l-4 border-[#8b6f47]">
                <i className="ri-double-quotes-l text-[#c9a96e] text-2xl mb-3 block"></i>
                <p
                  className="text-[#5a4e45] leading-[1.75] mb-4 text-[0.9375rem]"
                  style={{ fontFamily: "'Noto Serif TC', serif" }}
                >
                  透過 LINE 諮詢後，顧問幫我找到了最適合的天珠，完全符合我的需求！服務非常貼心，回覆也很快速。
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#e8e0d5] flex items-center justify-center">
                    <i className="ri-user-line text-[#8b6f47] text-sm"></i>
                  </div>
                  <span
                    className="text-[#8b6f47] font-medium text-[0.875rem]"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    陳小姐 · 台北
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
